import React, { useEffect, useMemo, useRef, useState } from "react";
import { AutoCompleteComponent } from "@syncfusion/ej2-react-dropdowns";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { get } from "lodash";
import useUtils from "services/utils/useUtils";

interface IInputProps {
  name: string;
  defaultValue?: number | string | null;
  required?: string;
  disabled?: boolean;
  xl?: number;
  md?: number;
  col?: number;
  contingenciaCodigo?: string | number | null;
}

interface CausaPagoOption {
  Codigo: string;
  Descripcion: string;
  ContingenciaCodigo: string;
}

type SyncfusionDataSourceItem = { [key: string]: Object };

const normalizeCode = (value: unknown): string => String(value ?? "").trim();

const isDigitsOnly = (value: string): boolean => /^[0-9]+$/.test(value);

const areCodesEquivalent = (left: unknown, right: unknown): boolean => {
  const a = normalizeCode(left);
  const b = normalizeCode(right);

  if (!a || !b) return false;
  if (a === b) return true;

  if (isDigitsOnly(a) && isDigitsOnly(b)) {
    return String(Number(a)) === String(Number(b));
  }

  return false;
};

const sortByDescription = (left: CausaPagoOption, right: CausaPagoOption): number =>
  left.Descripcion.localeCompare(right.Descripcion, "es", { sensitivity: "base" });

const getValueByKeys = (source: Record<string, unknown>, keys: string[]): unknown => {
  const normalized = Object.entries(source).map(([k, v]) => [
    k.replace(/[_\s]/g, "").toLowerCase(),
    v,
  ] as const);

  for (const key of keys) {
    const target = key.replace(/[_\s]/g, "").toLowerCase();
    const match = normalized.find(([k]) => k === target);
    if (match) return match[1];
  }

  return undefined;
};

const extractRows = (response: unknown): Record<string, unknown>[] => {
  if (Array.isArray(response)) return response as Record<string, unknown>[];
  if (!response || typeof response !== "object") return [];

  const src = response as Record<string, unknown>;
  const candidates = [
    src.items,
    src.Items,
    src.result,
    src.Result,
    src.data,
    src.Data,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as Record<string, unknown>[];
  }

  return [];
};

const InputCausaDelPago: React.FC<IInputProps> = ({
  name,
  required,
  disabled = false,
  xl = 12,
  md = 12,
  col = 12,
  contingenciaCodigo = null,
}) => {
  const idCode = name;
  const idDesc = `${name}Descripcion`;

  const { getCausaPago } = useUtils();
  const {
    register,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  const [allData, setAllData] = useState<CausaPagoOption[]>([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [autoCompleteRefreshKey, setAutoCompleteRefreshKey] = useState(0);
  const autoCompleteRef = useRef<AutoCompleteComponent | null>(null);
  const currentCode = useWatch({ control, name: idCode, defaultValue: getValues(idCode) });
  const currentDesc = useWatch({ control, name: idDesc, defaultValue: getValues(idDesc) });

  const filteredData = useMemo(() => {
    if (!allData.length) return [];

    const expected = normalizeCode(contingenciaCodigo);
    const filtered = !expected ? allData : allData.filter((item) =>
      areCodesEquivalent(item.ContingenciaCodigo, expected)
    );
    const sourceData = filtered.length ? filtered : allData;
    const seenCodes = new Set<string>();

    return sourceData.filter((item) => {
      if (seenCodes.has(item.Codigo)) return false;
      seenCodes.add(item.Codigo);
      return true;
    });
  }, [allData, contingenciaCodigo]);

  useEffect(() => {
    register(idCode);
  }, [idCode, register]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const res = await getCausaPago();
      const rows = extractRows(res);
      const mappedData: CausaPagoOption[] = rows
        .map((row) => {
          const rawCodigo = getValueByKeys(row, ["codigo", "Codigo", "code"]);
          const rawDescripcion = getValueByKeys(row, [
            "descripcion",
            "Descripcion",
            "description",
          ]);
          const rawContingencia = getValueByKeys(row, [
            "contingenciaCodigo",
            "ContingenciaCodigo",
            "contingenciaId",
            "ContingenciaId",
          ]);

          return {
            Codigo: String(rawCodigo ?? "").trim(),
            Descripcion: String(rawDescripcion ?? "").trim(),
            ContingenciaCodigo: String(rawContingencia ?? "").trim(),
          };
        })
        .filter((item) => item.Codigo !== "" && item.Descripcion !== "")
        .sort(sortByDescription);

      if (isMounted) {
        setAllData(mappedData);
        setCatalogLoaded(true);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!catalogLoaded) {
      return;
    }

    const sourceData = filteredData.length ? filteredData : allData;
    const codeFromForm = normalizeCode(currentCode ?? getValues(idCode));
    const descFromForm = String(currentDesc ?? getValues(idDesc) ?? "");

    if (!sourceData.length) {
      if (!codeFromForm && !descFromForm) {
        setValue(idCode, null);
      }
      return;
    }

    if (codeFromForm) {
      const selectedByCode = sourceData.find((item) => areCodesEquivalent(item.Codigo, codeFromForm));
      if (selectedByCode) {
        if (descFromForm !== selectedByCode.Descripcion) {
          setValue(idDesc, selectedByCode.Descripcion);
          // Force Syncfusion AutoComplete to re-read controlled value after reset/hydration.
          setAutoCompleteRefreshKey((prev) => prev + 1);
        }
        if (!areCodesEquivalent(codeFromForm, selectedByCode.Codigo)) {
          setValue(idCode, selectedByCode.Codigo);
        }
        return;
      }
    }

    if (descFromForm) {
      const selectedByDesc = sourceData.find((item) => item.Descripcion === descFromForm);
      if (selectedByDesc) {
        if (!areCodesEquivalent(codeFromForm, selectedByDesc.Codigo)) {
          setValue(idCode, selectedByDesc.Codigo);
        }
        return;
      }
    }

    if (!codeFromForm && !descFromForm) {
      setValue(idCode, null);
    }
  }, [catalogLoaded, filteredData, allData, currentCode, currentDesc, idCode, idDesc, getValues, setValue]);

  const errorMessage = get(errors, idDesc)?.message as string | undefined;

  const openPopupOnFocus = (component: AutoCompleteComponent | null) => {
    if (disabled || !component || filteredData.length === 0) return;
    component.showPopup();
  };

  return (
    <div className={`col-xl-${xl} col-md-${md} col-${col}`}>
      <fieldset className={`input-block input-block--code ${errorMessage ? "error" : ""}`}>
        <div className="container-leyend">
          <legend className="input-block--code__legend">Causa del Pago Directo</legend>
        </div>

        <div className="row">
          <div
            className="col-12 delta-autocomplete"
            data-input-name={idDesc}
          >
            <Controller
              name={idDesc}
              control={control}
              defaultValue={getValues(idDesc) || null}
              rules={{ required }}
              render={({ field }) => (
                <AutoCompleteComponent
                  ref={(instance) => {
                    autoCompleteRef.current = instance;
                  }}
                  key={`causa-pago-directo-${autoCompleteRefreshKey}`}
                  id={idDesc}
                  dataSource={filteredData as unknown as SyncfusionDataSourceItem[]}
                  suggestionCount={filteredData.length}
                  fields={{ text: "Descripcion", value: "Descripcion" }}
                  placeholder="Seleccione la causa"
                  value={String(currentDesc ?? field.value ?? "")}
                  change={(e) => {
                    const selectedDesc = e?.value as string;
                    const selectedItem =
                      (e?.itemData as CausaPagoOption | undefined) ||
                      filteredData.find((item) => item.Descripcion === selectedDesc);

                    if (selectedItem) {
                      setValue(idCode, selectedItem.Codigo);
                      field.onChange(selectedItem.Descripcion);
                      return;
                    }

                    setValue(idCode, null);
                    field.onChange(selectedDesc && selectedDesc.trim() !== "" ? selectedDesc : null);
                  }}
                  onBlur={field.onBlur}
                  enabled={!disabled}
                  ignoreCase={true}
                  ignoreAccent={true}
                  filterType="Contains"
                  minLength={0}
                  focus={() => {
                    openPopupOnFocus(autoCompleteRef.current);
                  }}
                />
              )}
            />
          </div>
        </div>

        <input type="hidden" {...register(idCode)} />

        {errorMessage && <p className="error m-0">{errorMessage}</p>}
      </fieldset>
    </div>
  );
};

export default InputCausaDelPago;
